import { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import type { TargetProbeLayer, TryTargetProbeResponse } from '@nadoutong/contracts';
import { useTryTargetProbe } from '../../api/useTargetProbes.ts';
import { coerceErrorMessage } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Dialog, FormField, IconButton, Input, RadioGroup, Textarea, Tooltip } from '../../ui/index.ts';

type TryShape = 'ping' | 'arithmetic' | 'echo' | 'custom';

function isTryShape(value: string): value is TryShape {
  return value === 'ping' || value === 'arithmetic' || value === 'echo' || value === 'custom';
}

type Outcome =
  | { readonly kind: 'answer'; readonly result: TryTargetProbeResponse }
  | { readonly kind: 'error'; readonly message: string };

/**
 * 试打探针（无副作用）: pick a probe body, fire one shot at this routing
 * target, read the answer inline. Nothing else on the page changes — the
 * gateway writes no probe state for it (contrast 立即探测, which counts
 * toward the leg's probe state).
 */
export function TryProbeButton({
  layer,
  targetId,
  disabled = false,
}: {
  readonly layer: TargetProbeLayer;
  readonly targetId: string;
  readonly disabled?: boolean;
}): JSX.Element {
  const t = useT();
  const probe = useTryTargetProbe();
  const [open, setOpen] = useState(false);
  const [shape, setShape] = useState<TryShape>('ping');
  const [customText, setCustomText] = useState('');
  const [customMax, setCustomMax] = useState('16');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const label = t('probe_try.button');

  function send(): void {
    setOutcome(null);
    const max = Number(customMax.trim());
    probe.mutate(
      {
        layer,
        target_id: targetId,
        shape,
        ...(shape === 'custom'
          ? {
            custom_text: customText,
            ...(customMax.trim().length > 0 ? { custom_max_tokens: max } : {}),
          }
          : {}),
      },
      {
        onSuccess: (result) => setOutcome({ kind: 'answer', result }),
        onError: (error) => setOutcome({ kind: 'error', message: coerceErrorMessage(error) }),
      },
    );
  }

  const answer = outcome?.kind === 'answer' ? outcome.result : null;
  const answerOk = answer !== null && answer.status >= 200 && answer.status < 300;

  return (
    <>
      <Tooltip content={t('probe_try.tip')}>
        <IconButton
          label={label}
          size="sm"
          disabled={disabled}
          data-testid={`probe-try-open-${targetId}`}
          onClick={() => {
            setOutcome(null);
            setOpen(true);
          }}
        >
          <FlaskConical aria-hidden="true" className="h-3.5 w-3.5" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        size="sm"
        title={t('probe_try.title')}
        description={t('probe_try.description')}
        closeLabel={t('common.close')}
        testId={`probe-try-dialog-${targetId}`}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t('common.close')}</Button>
            <Button
              variant="primary"
              loading={probe.isPending}
              disabled={probe.isPending || (shape === 'custom' && customText.trim().length === 0)}
              onClick={send}
              data-testid={`probe-try-send-${targetId}`}
            >
              {t('probe_try.send')}
            </Button>
          </>
        )}
      >
        <div className="flex flex-col gap-3">
          <RadioGroup
            value={shape}
            onValueChange={(value) => {
              if (isTryShape(value)) setShape(value);
            }}
            orientation="horizontal"
            ariaLabel={t('guaranteed.probe_shape')}
            testId={`probe-try-shape-${targetId}`}
            options={[
              { value: 'ping', label: t('guaranteed.probe_shape_ping') },
              { value: 'arithmetic', label: t('guaranteed.probe_shape_arithmetic') },
              { value: 'echo', label: t('guaranteed.probe_shape_echo') },
              { value: 'custom', label: t('guaranteed.probe_shape_custom') },
            ]}
          />
          {shape === 'custom' ? (
            <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-2">
              <FormField label={t('guaranteed.probe_custom_text')} fieldId={`probe-try-text-${targetId}`}>
                <Textarea
                  id={`probe-try-text-${targetId}`}
                  rows={2}
                  maxLength={200}
                  value={customText}
                  onChange={(event) => setCustomText(event.currentTarget.value)}
                  data-testid={`probe-try-text-${targetId}`}
                />
              </FormField>
              <FormField label={t('guaranteed.probe_custom_max')} fieldId={`probe-try-max-${targetId}`}>
                <Input
                  id={`probe-try-max-${targetId}`}
                  mono
                  inputMode="numeric"
                  value={customMax}
                  onChange={(event) => setCustomMax(event.currentTarget.value)}
                  data-testid={`probe-try-max-${targetId}`}
                />
              </FormField>
            </div>
          ) : null}
          {outcome === null ? null : (
            <div
              className={answerOk ? 'text-dense text-ok' : 'text-dense text-bad'}
              data-testid={`probe-try-result-${targetId}`}
              data-ok={answerOk ? 'true' : 'false'}
            >
              {outcome.kind === 'error' ? outcome.message : (
                <>
                  <span className="font-mono">
                    {t('probe_try.result', {
                      code: String(outcome.result.status),
                      ms: String(outcome.result.latency_ms),
                      tokens: String(outcome.result.output_tokens),
                    })}
                  </span>
                  {outcome.result.excerpt === null ? null : (
                    <span className="mt-1 block break-all font-mono text-micro">{outcome.result.excerpt}</span>
                  )}
                  <span className="mt-1 block text-micro text-paper-low">
                    {`${outcome.result.upstream_name} · ${outcome.result.physical_model}`}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
